import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Megaphone, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCES = ['admin', 'manager', 'employee', 'customer', 'vip'];

const AdminAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', body: '', audience: ['employee', 'admin'], requires_ack: false });
  const [acks, setAcks] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/announcements`, { withCredentials: true });
      setItems(data);
    } catch { /* */ }
  };

  const toggleAudience = (a) => {
    setForm({ ...form, audience: form.audience.includes(a) ? form.audience.filter(x => x !== a) : [...form.audience, a] });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/announcements`, form, { withCredentials: true });
      toast.success('Announcement posted');
      setForm({ title: '', body: '', audience: ['employee', 'admin'], requires_ack: false });
      setShowForm(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true }); toast.success('Deleted'); refresh(); } catch { toast.error('Failed'); }
  };

  const loadAcks = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/announcements/${id}/acks`, { withCredentials: true });
      setAcks({ ...acks, [id]: data });
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-announcements-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Announcements</h2>
          <p className="text-[#A3A3A3]">Post company-wide updates and track acknowledgments</p>
        </div>
        <Button data-testid="new-announcement-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4 mr-2" /> New Announcement
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Title</Label>
            <Input data-testid="ann-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Body</Label>
            <Textarea data-testid="ann-body" rows={4} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Audience</Label>
            <div className="flex gap-2 flex-wrap">
              {AUDIENCES.map(a => (
                <button type="button" key={a} onClick={() => toggleAudience(a)} className={`px-3 py-1.5 rounded-md border text-xs uppercase tracking-wider ${form.audience.includes(a) ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>{a}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-white text-sm">
            <input data-testid="ann-req-ack" type="checkbox" checked={form.requires_ack} onChange={(e) => setForm({...form, requires_ack: e.target.checked})} className="accent-[#D4AF37]" />
            Require acknowledgment from recipients
          </label>
          <Button data-testid="ann-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Post</Button>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]"><Megaphone className="w-8 h-8 mx-auto mb-2 text-[#262626]" /> No announcements yet</div>
        ) : items.map((a) => (
          <div key={a.id} className="bg-[#141414] border border-[#262626] rounded-md p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1">
                <p className="text-white font-bold">{a.title}</p>
                <p className="text-[#A3A3A3] text-sm mt-1 whitespace-pre-wrap">{a.body}</p>
                <div className="flex gap-2 mt-3 text-xs text-[#525252]">
                  <span>{new Date(a.created_at).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>To: {a.audience.join(', ')}</span>
                  {a.requires_ack && <><span>•</span><span className="text-[#D4AF37]">Acknowledgment required</span></>}
                </div>
              </div>
              <div className="flex gap-2">
                {a.requires_ack && (
                  <Button onClick={() => loadAcks(a.id)} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]"><Users className="w-3 h-3 mr-1" /> View Acks</Button>
                )}
                <Button onClick={() => remove(a.id)} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
            {acks[a.id] && (
              <div className="mt-4 border-t border-[#262626] pt-3 space-y-1">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Acknowledged by {acks[a.id].length}:</p>
                {acks[a.id].length === 0 ? <p className="text-[#A3A3A3] text-xs">No acknowledgments yet</p> : acks[a.id].map(x => (
                  <p key={x.id} className="text-white text-sm">✓ {x.user_name} ({x.user_role}) — {new Date(x.acknowledged_at).toLocaleString()}</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAnnouncements;
